import { useApi, useLoad } from "@allergy123/api-client/react";
import { SUPPLY_ORDER_STATUSES, type StaffSupplyOrder, type SupplyOrderStatus } from "@allergy123/contracts";
import { Button, Card, Field, Select, Status, Table, Td, Th } from "@allergy123/ui";
import { Fragment, useState } from "react";
import { Link } from "react-router";
import { BILLING_PLAN_LABELS, billingErrorText, centsFromDollars, dollars, isStale, ORDER_STATUS_LABELS, shortDate } from "./billing/common";

type Filter = SupplyOrderStatus | "all";

/** The one row whose inline form is open, and what has been typed into it. */
interface Open {
  orderId: string;
  mode: "confirm" | "ship" | "cancel";
  shipping: string;
  carrier: string;
  trackingNumber: string;
}

const FILTER_OPTIONS: { value: Filter; label: string }[] = [
  { value: "received", label: "Received" },
  ...SUPPLY_ORDER_STATUSES.filter((s) => s !== "received").map((s) => ({ value: s, label: ORDER_STATUS_LABELS[s] })),
  { value: "all", label: "All" },
];

const PAYMENT_LABELS: Record<StaffSupplyOrder["paymentStatus"], string> = { unpaid: "Unpaid", processing: "Processing", paid: "Paid", failed: "Failed" };

/**
 * Supply orders across every organization (design 2026-10-25, ADR 0008):
 * received → confirmed with shipping, which charges the saved payment method
 * through one Stripe invoice → shipped with tracking. Cancelling a confirmed
 * order does not refund its invoice; that stays a staff action in Stripe.
 */
export function OrdersPage() {
  const api = useApi();
  const [filter, setFilter] = useState<Filter>("received");
  const orders = useLoad((a) => a.admin.billing.orders.list(filter === "all" ? {} : { status: filter }), [filter]);
  // The order contract names a location only by id; the names come from each organization's member page, which lists its locations.
  const organizationIds = [...new Set((orders.data?.orders ?? []).map((o) => o.organizationId))].sort();
  const locations = useLoad(
    async (a) => {
      const names: Record<string, string> = {};
      const lists = await Promise.all(organizationIds.map((id) => a.admin.members(id).catch(() => ({ locations: [] }))));
      for (const list of lists) for (const l of list.locations) names[l.id] = l.name;
      return names;
    },
    [organizationIds.join(",")],
  );
  const [open, setOpen] = useState<Open | null>(null);
  const [msg, setMsg] = useState<{ kind: "info" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const openFor = (o: StaffSupplyOrder, mode: Open["mode"]) => setOpen({ orderId: o.id, mode, shipping: o.pricingTier === "cor" ? "0" : "", carrier: "", trackingNumber: "" });

  async function run(action: () => Promise<string>) {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const text = await action();
      setMsg({ kind: "info", text });
      setOpen(null);
      orders.reload();
    } catch (err) {
      setMsg({ kind: "error", text: billingErrorText(err) });
      if (isStale(err)) {
        setOpen(null);
        orders.reload();
      }
    } finally {
      setBusy(false);
    }
  }

  const confirm = (o: StaffSupplyOrder, shippingCents: number) =>
    void run(async () => {
      const res = await api.admin.billing.orders.confirm(o.id, { shippingCents, version: o.version });
      return `Confirmed ${o.orderNumber}: ${dollars(res.order.totalCents)} charged to the saved payment method (invoice ${res.order.stripeInvoiceId ?? "pending"}).`;
    });
  const ship = (o: StaffSupplyOrder, carrier: string, trackingNumber: string) =>
    void run(async () => {
      await api.admin.billing.orders.ship(o.id, { carrier, trackingNumber, version: o.version });
      return `Marked ${o.orderNumber} shipped with ${carrier} ${trackingNumber}. The practice is being notified.`;
    });
  const cancel = (o: StaffSupplyOrder) =>
    void run(async () => {
      await api.admin.billing.orders.cancel(o.id, { version: o.version });
      return o.status === "confirmed" ? `Cancelled ${o.orderNumber}. Its invoice${o.stripeInvoiceId ? ` ${o.stripeInvoiceId}` : ""} is not refunded here; settle that in Stripe.` : `Cancelled ${o.orderNumber}.`;
    });

  const count = orders.data?.orders.length;
  const locationName = (id: string) => locations.data?.[id] ?? <span className="text-small text-ink-secondary" title={id}>{id.slice(0, 8)}…</span>;

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-h1 font-semibold text-anchor">Orders</h1>
        <p className="max-w-[68ch] text-ink-secondary">Supply orders placed by practices, newest first. Confirming an order charges the practice's saved payment method through one Stripe invoice; marking it shipped tells the practice the carrier and tracking number. Every change here is audited.</p>
      </div>
      {orders.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load orders (${billingErrorText(orders.error)})`} /></div>}
      {msg && <div className="mb-6"><Status kind={msg.kind} label={msg.text} /></div>}
      <Card>
        <div className="mb-4">
          <Select label="Status" value={filter} onChange={(e) => { setFilter(e.target.value as Filter); setOpen(null); setMsg(null); }} options={FILTER_OPTIONS} />
        </div>
        <Table caption={count !== undefined ? `${count} order${count === 1 ? "" : "s"}` : orders.state === "error" ? "Not loaded" : "Loading…"}>
          <thead>
            <tr>
              <Th>Order</Th>
              <Th>Organization</Th>
              <Th>Location</Th>
              <Th>Product</Th>
              <Th>Placed</Th>
              <Th>Status</Th>
              <Th>Payment</Th>
              <Th><span className="sr-only">Actions</span></Th>
            </tr>
          </thead>
          <tbody>
            {count === 0 && (
              <tr>
                <Td colSpan={8} className="text-ink-secondary">{filter === "all" ? "No orders have been placed yet." : `No ${ORDER_STATUS_LABELS[filter].toLowerCase()} orders.`}</Td>
              </tr>
            )}
            {orders.data?.orders.map((o) => {
              const isOpen = open?.orderId === o.id ? open : null;
              const isCore = o.pricingTier === "cor";
              const shippingCents = isCore ? 0 : centsFromDollars(isOpen?.shipping ?? "");
              const linesCents = o.lines.reduce((sum, l) => sum + l.unitCents * l.quantity, 0);
              return (
                <Fragment key={o.id}>
                  <tr>
                    <Td>
                      <div className="font-medium whitespace-nowrap">{o.orderNumber}</div>
                      <div className="text-small text-ink-secondary">{BILLING_PLAN_LABELS[o.pricingTier]} prices{o.source === "auto_reorder" ? " · automatic reorder" : ""}</div>
                      {o.stripeInvoiceId && <div className="text-small text-ink-secondary">{o.stripeInvoiceId}</div>}
                    </Td>
                    <Td>
                      {o.organizationName ?? <span className="text-ink-secondary">Unknown</span>}
                      <div className="text-small"><Link to={`/organizations/${o.organizationId}/billing`} className="text-anchor underline">Billing</Link></div>
                    </Td>
                    <Td>{locationName(o.locationId)}</Td>
                    <Td>
                      {o.lines.map((l) => (
                        <div key={l.sku}>
                          {l.name} × {l.quantity}
                          <span className="text-small text-ink-secondary"> at {dollars(l.unitCents)}</span>
                        </div>
                      ))}
                      <div className="text-small text-ink-secondary">{o.status === "received" ? `${dollars(linesCents)} before shipping` : `${dollars(o.totalCents)} with ${dollars(o.shippingCents)} shipping`}</div>
                    </Td>
                    <Td className="whitespace-nowrap">{shortDate(o.createdAt)}</Td>
                    <Td>
                      {ORDER_STATUS_LABELS[o.status]}
                      {o.status === "shipped" && o.carrier && <div className="text-small text-ink-secondary">{o.carrier} {o.trackingNumber}</div>}
                    </Td>
                    <Td>{PAYMENT_LABELS[o.paymentStatus]}</Td>
                    <Td>
                      {!isOpen && (
                        <div className="flex flex-wrap gap-2">
                          {o.status === "received" && <Button variant="secondary" surface="card" onClick={() => openFor(o, "confirm")} disabled={busy}>Confirm</Button>}
                          {o.status === "confirmed" && <Button variant="secondary" surface="card" onClick={() => openFor(o, "ship")} disabled={busy}>Mark shipped</Button>}
                          {(o.status === "received" || o.status === "confirmed") && <Button variant="secondary" surface="card" onClick={() => openFor(o, "cancel")} disabled={busy}>Cancel</Button>}
                        </div>
                      )}
                    </Td>
                  </tr>
                  {isOpen && (
                  <tr>
                    <Td colSpan={8} className="bg-card-tinted">
                      {isOpen.mode !== "cancel" && <p className="mb-3 font-medium">{isOpen.mode === "confirm" ? `Confirm ${o.orderNumber}` : `Mark ${o.orderNumber} shipped`}</p>}
                      {isOpen.mode === "confirm" && (
                        <form
                          className="grid max-w-[520px] gap-3"
                          onSubmit={(e) => {
                            e.preventDefault();
                            if (shippingCents !== null) confirm(o, shippingCents);
                          }}
                        >
                          <Field
                            label="Shipping (dollars)"
                            inputMode="decimal"
                            value={isOpen.shipping}
                            onChange={(e) => setOpen({ ...isOpen, shipping: e.target.value })}
                            disabled={isCore}
                            placeholder="0.00"
                            hint={isCore ? "Core includes shipping, so none is charged." : "Typed in dollars; sent to Stripe in cents."}
                            error={!isCore && isOpen.shipping !== "" && shippingCents === null ? "A dollar amount, like 12.50." : undefined}
                          />
                          <p className="text-small">Total to charge: <strong>{shippingCents === null ? "—" : dollars(linesCents + shippingCents)}</strong></p>
                          <div className="flex flex-wrap gap-2">
                            <Button type="submit" disabled={busy || shippingCents === null}>{busy ? "Confirming…" : "Confirm and charge the saved payment method"}</Button>
                            <Button variant="secondary" surface="card" onClick={() => setOpen(null)} disabled={busy}>Back</Button>
                          </div>
                        </form>
                      )}
                      {isOpen.mode === "ship" && (
                        <form
                          className="grid max-w-[520px] gap-3"
                          onSubmit={(e) => {
                            e.preventDefault();
                            ship(o, isOpen.carrier.trim(), isOpen.trackingNumber.trim());
                          }}
                        >
                          <Field label="Carrier" value={isOpen.carrier} onChange={(e) => setOpen({ ...isOpen, carrier: e.target.value })} required maxLength={80} placeholder="UPS" />
                          <Field label="Tracking number" value={isOpen.trackingNumber} onChange={(e) => setOpen({ ...isOpen, trackingNumber: e.target.value })} required maxLength={120} />
                          <div className="flex flex-wrap gap-2">
                            <Button type="submit" disabled={busy || !isOpen.carrier.trim() || !isOpen.trackingNumber.trim()}>{busy ? "Saving…" : "Mark shipped"}</Button>
                            <Button variant="secondary" surface="card" onClick={() => setOpen(null)} disabled={busy}>Back</Button>
                          </div>
                        </form>
                      )}
                      {isOpen.mode === "cancel" && (
                        <div className="grid max-w-[520px] gap-2">
                          <p className="text-small">
                            Cancel {o.orderNumber}?{o.status === "confirmed" ? " Its invoice has already been charged and is not refunded here; settle that in Stripe." : " The practice is not charged."}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Button onClick={() => cancel(o)} disabled={busy}>{busy ? "Cancelling…" : "Yes, cancel it"}</Button>
                            <Button variant="secondary" surface="card" onClick={() => setOpen(null)} disabled={busy}>Keep it</Button>
                          </div>
                        </div>
                      )}
                    </Td>
                  </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </Card>
    </>
  );
}
